import React, { FC, useMemo } from 'react';
import { styled } from '@mui/material';
import { Divider, Paper, Theme, Typography } from '@mui/material';
import { Skeleton } from '@mui/material';
import { currencyFormat } from 'common/formats/formats';
import { formatNumbers } from 'utilities';
import { EnterpriseValueProps } from './types';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: '1em',
  textAlign: 'center',
  minWidth: '200px',
}));

const EnterpriseValueText = styled('small')(({ theme }) => ({
  textTransform: 'uppercase',
  fontWeight: 700,
  fontSize: '1.225em',
}));

const EnterpriseValueSkeleton = styled(Skeleton)({
  width: '20%',
  margin: 'auto',
});

const StyledDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  height: 2,
  width: '20%',
  margin: '0px auto',
}));

const ValuePerCommonText = styled('p')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '3.125em',
  fontWeight: 700,
}));

const ValuePerCommonSkeleton = styled(Skeleton)({
  width: '20%',
  margin: 'auto',
});

const TitleTypography = styled(Typography)({
  fontSize: '1.005em',
});

const EnterpriseValue: FC<EnterpriseValueProps> = ({ enterpriseData, isLoading, format }) => {
  const { enterpriseValue, valuePerCommon } = useMemo(() => {
    if (enterpriseData && !isLoading && format) {
      const {
        weighted_market_enterprise_value: enterpriseValueData,
        marketable_value_per_common_share: valuePerCommonData,
      } = enterpriseData;

      const { currency } = format;

      const enterpriseValueFormated = formatNumbers({
        format: currencyFormat({ fractionDigits: 0 }),
        value: enterpriseValueData,
        prefix: null,
        suffix: null,
        allowNegativeValue: true,
        currency,
        currencyCode: currency.code,
        blankIfNull: false,
        staticCurrency: null,
      });

      const valuePerCommonFormated = formatNumbers({
        format: currencyFormat({ fractionDigits: 2 }),
        value: valuePerCommonData,
        prefix: null,
        suffix: null,
        allowNegativeValue: true,
        currency,
        currencyCode: currency.code,
        blankIfNull: false,
        staticCurrency: null,
      });

      return { enterpriseValue: enterpriseValueFormated, valuePerCommon: valuePerCommonFormated };
    }

    return { enterpriseValue: null, valuePerCommon: null };
  }, [enterpriseData, isLoading, format]);

  return (
    <StyledPaper>
      {!isLoading && enterpriseValue ? (
        <EnterpriseValueText>{enterpriseValue}</EnterpriseValueText>
      ) : (
        <EnterpriseValueSkeleton variant="text" />
      )}
      <TitleTypography>Enterprise Value</TitleTypography>
      <StyledDivider />
      {!isLoading && valuePerCommon ? (
        <ValuePerCommonText>{valuePerCommon}</ValuePerCommonText>
      ) : (
        <ValuePerCommonSkeleton variant="text" />
      )}
      <Typography>Value per Common Share</Typography>
    </StyledPaper>
  );
};

export default EnterpriseValue;
